"use client";

import { c as _c } from "react/compiler-runtime";
import { useId, useMemo, useState } from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import { answerSegments } from "@/lib/knowledge/answerLinks";
import styles from "@/app/(public)/faqs/FaqPage.module.css";
export interface FaqDirectoryItem {
  id: string;
  heading: string;
  description: string;
  /** Answer text with `[label](/path)` links; `description` stays the plain text used for search. */
  answerMarkup?: string;
  topic: string;
}
interface FaqSearchProps {
  items: FaqDirectoryItem[];
  listId: string;
}
interface FaqTopic {
  label: string;
  count: number;
}
const ALL_TOPICS = "All";
const TOPIC_ORDER = ["About THIScar", "Buying a Car", "Financing", "Returns & Policies", "Sell or Trade", "Locations & Support"];
const PRIVATE_SALE_GUIDE_PATH = "/sell-my-car/sell-it-yourself";
function FaqAnswer(t0) {
  const $ = _c(6);
  const {
    item
  } = t0;
  if (!item.answerMarkup) {
    let t1;
    if ($[0] !== item.description) {
      t1 = <>{item.description}</>;
      $[0] = item.description;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== item.answerMarkup) {
    t1 = answerSegments(item.answerMarkup).map(_temp);
    $[2] = item.answerMarkup;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <>{t1}</>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function _temp(segment, index) {
  return segment.href ? <a key={index} href={segment.href} onClick={() => {
    if (segment.href.split(/[?#]/)[0] === PRIVATE_SALE_GUIDE_PATH) {
      sendGTMEvent({
        event: "private_sale_faq_guide_click",
        source_page: "/faqs"
      });
    }
  }}>{segment.text}</a> : segment.text;
}
export function FaqSearch(t0) {
  const $ = _c(49);
  const {
    items,
    listId
  } = t0;
  const inputId = useId();
  const statusId = useId();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTopic, setActiveTopic] = useState(ALL_TOPICS);
  let t1;
  if ($[0] !== items) {
    const topicCounts = new Map();
    items.forEach(item => {
      topicCounts.set(item.topic, (topicCounts.get(item.topic) || 0) + 1);
    });
    let t2;
    if ($[2] !== items.length) {
      t2 = {
        label: ALL_TOPICS,
        count: items.length
      };
      $[2] = items.length;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = [t2, ...TOPIC_ORDER.flatMap(label => {
      const count = topicCounts.get(label) || 0;
      return count > 0 ? [{
        label,
        count
      }] : [];
    })];
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const topics = t1;
  let t2;
  if ($[4] !== activeTopic || $[5] !== items || $[6] !== searchTerm) {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    t2 = items.filter(item_0 => {
      const matchesTopic = activeTopic === ALL_TOPICS || item_0.topic === activeTopic;
      const matchesSearch = normalizedSearchTerm.length === 0 || `${item_0.heading} ${item_0.description}`.toLowerCase().includes(normalizedSearchTerm);
      return matchesTopic && matchesSearch;
    });
    $[4] = activeTopic;
    $[5] = items;
    $[6] = searchTerm;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const filteredItems = t2;
  const answerLabel = filteredItems.length === 1 ? "answer" : "answers";
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className={styles.eyebrow}>Questions, answered</p>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h1 id="faq-page-title" className={styles.heroTitle}><span>You have real questions.</span><span>We have straight answers.</span></h1>;
    t5 = <p className={styles.heroDescription}>Buying, selling, trading, financing, delivery—or simply wondering what makes THIScar different? Start here.</p>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  let t6;
  if ($[11] !== inputId) {
    t6 = <label htmlFor={inputId} className={styles.srOnly}>Search frequently asked questions</label>;
    $[11] = inputId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = event => setSearchTerm(event.target.value);
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== inputId || $[16] !== listId || $[17] !== searchTerm || $[18] !== statusId) {
    t9 = <input id={inputId} type="search" value={searchTerm} onChange={t8} placeholder="Search questions or answers" aria-controls={listId} aria-describedby={statusId} />;
    $[15] = inputId;
    $[16] = listId;
    $[17] = searchTerm;
    $[18] = statusId;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t9) {
    t10 = <section className={styles.hero} aria-labelledby="faq-page-title"><div className={styles.heroInner}>{t3}{t4}{t5}<div className={styles.search} role="search">{t6}{t7}{t9}</div></div></section>;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p>Explore by topic</p>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== activeTopic || $[25] !== topics) {
    t12 = <aside className={styles.topicRail} aria-label="Explore FAQs by topic">{t11}<div className={styles.topicScroller}>{topics.map(topic => {
          const isActive = topic.label === activeTopic;
          return <button key={topic.label} type="button" className={isActive ? styles.activeTopic : undefined} onClick={() => setActiveTopic(topic.label)} aria-pressed={isActive}><span>{topic.label}</span><small>{topic.count}</small></button>;
        })}</div></aside>;
    $[24] = activeTopic;
    $[25] = topics;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== activeTopic) {
    t13 = <h2 id="faq-list-title">{activeTopic}</h2>;
    $[27] = activeTopic;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== answerLabel || $[30] !== filteredItems.length || $[31] !== statusId) {
    t14 = <span id={statusId} aria-live="polite">{filteredItems.length} {answerLabel}</span>;
    $[29] = answerLabel;
    $[30] = filteredItems.length;
    $[31] = statusId;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t13 || $[34] !== t14) {
    t15 = <div className={styles.resultsHeader}>{t13}{t14}</div>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== filteredItems || $[37] !== listId) {
    t16 = filteredItems.length > 0 ? <div id={listId} className={styles.accordion}>{filteredItems.map(_temp2)}</div> : <div id={listId} className={styles.noResults}><h3>No matching answers found.</h3><p>Try another search or choose a different topic.</p><button type="button" onClick={() => {
        setSearchTerm("");
        setActiveTopic(ALL_TOPICS);
      }}>Show all questions</button></div>;
    $[36] = filteredItems;
    $[37] = listId;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t15 || $[40] !== t16) {
    t17 = <div className={styles.results}>{t15}{t16}</div>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t12 || $[43] !== t17) {
    t18 = <section className={styles.faqBody} aria-labelledby="faq-list-title">{t12}{t17}</section>;
    $[42] = t12;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <section className={styles.contact} aria-labelledby="faq-contact-title"><div><p className={styles.contactEyebrow}>Still have questions?</p><h2 id="faq-contact-title">How can I be helpful?</h2><p>That’s how we start every conversation. Talk to a real person who is ready to help.</p><a href="tel:18008447227">Talk to Us</a></div></section>;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t10 || $[47] !== t18) {
    t20 = <>{t10}{t18}{t19}</>;
    $[46] = t10;
    $[47] = t18;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
}
function _temp2(item_1) {
  return <details key={item_1.id} itemScope={true} itemProp="mainEntity" itemType="https://schema.org/Question"><summary itemProp="name"><span>{item_1.heading}</span><span className={styles.toggle} aria-hidden="true">+</span></summary><div itemScope={true} itemProp="acceptedAnswer" itemType="https://schema.org/Answer"><p itemProp="text" className="t-whitespace-pre-wrap"><FaqAnswer item={item_1} /></p></div></details>;
}